<script setup>
import { onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useMainStore } from '@/stores/main'
import { useDashboardStore } from '@/stores/dashboard'
import DashboardService from '@/api/Dashboard'
import ForkDashboardModal from '@/components/dashboard/ForkDashboardModal.vue'

const props = defineProps({
  onFork: {
    type: Function,
    default: null
  },
  onSubscribe: {
    type: Function,
    default: null
  }
})

const toast = useToast()
const mainStore = useMainStore()
const dashboardStore = useDashboardStore()

const visibilityLabels = {
  1: 'Organization',
  2: 'Public'
}

onMounted(() => {
  dashboardStore.fetchBrowseDashboards()
})

async function handleSubscribe(dashboard) {
  try {
    await dashboardStore.subscribeToDashboard(dashboard.id)
    await dashboardStore.fetchBrowseDashboards()
    toast.success(`Subscribed to "${dashboard.name}"`)
    if (props.onSubscribe) {
      props.onSubscribe(dashboard)
    }
  } catch (e) {
    toast.error(e.message || 'Failed to subscribe')
  }
}

async function handleUnsubscribe(dashboard) {
  try {
    await dashboardStore.unsubscribeFromDashboard(dashboard.id)
    toast.success(`Unsubscribed from "${dashboard.name}"`)
  } catch (e) {
    toast.error(e.message || 'Failed to unsubscribe')
  }
}

async function handleFork(dashboard) {
  // Fetch full dashboard data (with widgets + saved searches) for the preview modal
  try {
    const response = await DashboardService.get(dashboard.id)
    if (response.status !== 'success' || !response.data) {
      toast.error(response.message || 'Failed to load dashboard details')
      return
    }

    mainStore.show_modal(ForkDashboardModal, {
      dashboard: response.data,
      isSubscribed: dashboard.relationship === 'subscribed',
      onFork: (forked) => {
        toast.success(`Forked "${dashboard.name}"`)
        if (props.onFork) {
          props.onFork(forked)
        }
        mainStore.close_slideover()
      }
    })
  } catch (e) {
    toast.error(e.message || 'Failed to fork dashboard')
  }
}
</script>

<template>
  <div class="browse-slideover">
    <div class="slideover-header">
      <h2 class="slideover-title">Browse Dashboards</h2>
      <span v-if="!dashboardStore.browseLoading" class="count-badge">
        {{ dashboardStore.browseDashboards.length }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="dashboardStore.browseLoading" class="loading-state">
      <font-awesome-icon icon="spinner" spin />
      Loading shared dashboards...
    </div>

    <!-- Empty -->
    <div v-else-if="dashboardStore.browseDashboards.length === 0" class="empty-state">
      <font-awesome-icon :icon="['fas', 'compass']" class="empty-icon" />
      <p class="empty-title">No shared dashboards</p>
      <p class="empty-description">Dashboards shared within your organization or publicly will appear here.</p>
    </div>

    <!-- Dashboard List -->
    <div v-else class="dashboards-list">
      <div
        v-for="dashboard in dashboardStore.browseDashboards"
        :key="dashboard.id"
        class="dashboard-card"
      >
        <div class="card-header">
          <div class="card-info">
            <span class="card-name">{{ dashboard.name }}</span>
            <span class="visibility-badge" :class="'visibility-' + dashboard.visibility">
              {{ visibilityLabels[dashboard.visibility] || 'Shared' }}
            </span>
          </div>
          <span v-if="dashboard.owner_name" class="card-owner">by {{ dashboard.owner_name }}</span>
        </div>

        <p v-if="dashboard.description" class="card-description">
          {{ dashboard.description.length > 120 ? dashboard.description.slice(0, 120) + '...' : dashboard.description }}
        </p>

        <div class="card-actions">
          <button
            v-if="dashboard.relationship === 'subscribed'"
            class="btn-action btn-unsubscribe"
            @click="handleUnsubscribe(dashboard)"
          >
            <font-awesome-icon :icon="['fas', 'link-slash']" />
            Unsubscribe
          </button>
          <button
            v-else
            class="btn-action btn-subscribe"
            @click="handleSubscribe(dashboard)"
          >
            <font-awesome-icon :icon="['fas', 'link']" />
            Subscribe
          </button>

          <button
            class="btn-action btn-fork"
            @click="handleFork(dashboard)"
          >
            <font-awesome-icon :icon="['fas', 'code-fork']" />
            Fork
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.browse-slideover {
  padding: 1.5rem;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.slideover-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
}

.slideover-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #f3f4f6;
  margin: 0;
  font-family: 'Jura', sans-serif;
}

.count-badge {
  background: rgba(115, 87, 210, 0.2);
  color: #a78bfa;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
}

/* Loading */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem;
  color: #9ca3af;
  font-size: 0.9375rem;
}

/* Empty */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
}

.empty-icon {
  font-size: 2.5rem;
  color: #6b7280;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #9ca3af;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
}

/* Dashboard List */
.dashboards-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dashboards-list::-webkit-scrollbar {
  width: 6px;
}

.dashboards-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
}

.dashboards-list::-webkit-scrollbar-thumb {
  background: #3E4A56;
  border-radius: 3px;
}

.dashboard-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  padding: 1rem;
  transition: border-color 0.2s ease;
}

.dashboard-card:hover {
  border-color: rgba(115, 87, 210, 0.2);
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.card-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.card-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #f3f4f6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.visibility-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}

.visibility-1 {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}

.visibility-2 {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}

.card-owner {
  font-size: 0.8125rem;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}

.card-description {
  font-size: 0.8125rem;
  color: #9ca3af;
  line-height: 1.4;
  margin: 0.25rem 0 0 0;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.btn-action {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-subscribe {
  background: rgba(115, 87, 210, 0.15);
  border-color: rgba(115, 87, 210, 0.25);
  color: #a78bfa;
}

.btn-subscribe:hover {
  background: rgba(115, 87, 210, 0.25);
  border-color: rgba(115, 87, 210, 0.4);
}

.btn-unsubscribe {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.btn-unsubscribe:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.35);
}

.btn-fork {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #9ca3af;
}

.btn-fork:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
  color: #e5e7eb;
}
</style>
